import { useEffect, useMemo, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { ExpandMore, Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod';
import {
  registerSchema as baseRegisterSchema,
  type LoginFormData,
  loginSchema,
  type RegisterFormData,
} from './schema';
import { ContentBox } from '@/components/shared/content-box';
import { Api, LocalStorage } from '@/constants';
import { useNotification } from '@/context/notification';
import { session_path } from '@/utils/routes';
import {
  useGetCompanyByCode,
  useLogin,
  useNewRegister,
} from '@/data/auth/ticket-register';

const getAsset = (asset: string) => {
  return `${Api.EdupassApi}/contents/assets/${asset}`;
};

const getSvg = (svg: string) => {
  return `${Api.EdupassApi}/contents/svg/${svg}`;
};

type ExpandedSection = 'register' | 'login' | null;

export const TicketRegister = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useNotification();
  const [companyCode, setCompanyCode] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<ExpandedSection>(null);
  const [showPassword, setShowPassword] = useState(false);

  const { data: company } = useGetCompanyByCode(companyCode);
  const { mutate: newRegister, isPending: isRegisterPending } =
    useNewRegister();
  const { login, isPending: isLoginPending } = useLogin();

  const registerSchemaWithEmployeeCode = useMemo(() => {
    if (company?.check_employee_code) {
      return baseRegisterSchema.extend({
        employee_code: z.string().min(1, 'Campo obrigatório'),
      });
    }
    return baseRegisterSchema;
  }, [company?.check_employee_code]);

  const {
    control: registerControl,
    handleSubmit: handleRegisterSubmit,
    formState: { errors: registerErrors },
    reset: resetRegister,
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchemaWithEmployeeCode),
    defaultValues: {
      name: '',
      email: '',
      company: '',
      employee_code: '',
    },
  });

  const {
    control: loginControl,
    handleSubmit: handleLoginSubmit,
    formState: { errors: loginErrors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const checkRedirectAfterLogin = () => {
    const redirectAfterLogin = localStorage.getItem(
      LocalStorage.RedirectAfterLogin
    );

    if (redirectAfterLogin) {
      localStorage.removeItem(LocalStorage.RedirectAfterLogin);
      navigate(redirectAfterLogin);
      return;
    }

    navigate('/');
  };

  const onRegisterSubmit = (data: RegisterFormData) => {
    if (!companyCode) return;

    newRegister(
      {
        email: data.email || '',
        name: data.name || '',
        company: data.company || '',
        registerCompany: companyCode,
        employee_code: data.employee_code || '',
      },
      {
        onSuccess: () => {
          resetRegister();
          checkRedirectAfterLogin();
        },
        onError: () => {
          toast({
            content: 'Erro ao realizar cadastro',
            type: 'error',
          });
        },
      }
    );
  };

  const onLoginSubmit = ({ email, password }: LoginFormData) => {
    login(
      { email, password },
      {
        onSuccess: checkRedirectAfterLogin,
        onError: () => {
          toast({
            content: 'Usuário ou senha inválidos',
            type: 'error',
          });
        },
      }
    );
  };

  const onForgotPassword = () => {
    navigate('/login/esqueci-minha-senha');
  };

  useEffect(() => {
    const origin = searchParams.get('origin');
    if (!origin) {
      navigate(session_path());
      return;
    }
    setTimeout(() => {
      setCompanyCode(origin);
    }, 0);
  }, [searchParams, navigate]);

  if (!companyCode) return null;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        overflow: 'auto',
        backgroundImage: `url('${getAsset('new-register-logo.png')}')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        backgroundColor: 'rgba(56, 0, 72, 1)',
      }}
    >
      <Container maxWidth="lg">
        <Stack spacing={4} py={4}>
          <Stack alignItems="center">
            <img
              src={getAsset('new-register-galena-logo.svg')}
              alt="Galena Logo"
              style={{ maxWidth: '20%', margin: '16px' }}
            />
          </Stack>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, lg: 6 }}>
              <ContentBox>
                <Stack alignItems="center" p={5} spacing={2}>
                  <Typography variant="h2" mb={2}>
                    É seu primeiro acesso?
                  </Typography>

                  {expanded !== 'register' ? (
                    <Button
                      variant="outlined"
                      endIcon={<ExpandMore />}
                      onClick={() => setExpanded('register')}
                    >
                      Cadastre-se aqui
                    </Button>
                  ) : (
                    <Box
                      component="form"
                      onSubmit={handleRegisterSubmit(onRegisterSubmit)}
                      width="100%"
                      sx={{
                        maxHeight: expanded === 'register' ? '600px' : '0',
                        overflow: 'hidden',
                        transition: 'max-height 0.2s ease-out',
                      }}
                    >
                      <Typography mb={3}>
                        Caso queira fazer o seu cadastro, por favor preencha as
                        informações abaixo
                      </Typography>

                      <Stack spacing={3}>
                        <Controller
                          name="name"
                          control={registerControl}
                          render={({ field }) => (
                            <Stack>
                              <FormLabel>Qual é o seu nome completo</FormLabel>
                              <TextField
                                {...field}
                                placeholder="Nome Sobrenome"
                                error={!!registerErrors.name}
                                helperText={registerErrors.name?.message}
                              />
                            </Stack>
                          )}
                        />

                        <Controller
                          name="email"
                          control={registerControl}
                          render={({ field }) => (
                            <Stack>
                              <FormLabel>
                                Qual e-mail você mais utiliza?
                              </FormLabel>
                              <TextField
                                {...field}
                                type="email"
                                placeholder="nome@exemplo.com.br"
                                error={!!registerErrors.email}
                                helperText={registerErrors.email?.message}
                              />
                            </Stack>
                          )}
                        />

                        <Controller
                          name="company"
                          control={registerControl}
                          render={({ field }) => (
                            <Stack>
                              <FormLabel>
                                Em qual empresa você trabalha?
                              </FormLabel>
                              <TextField
                                {...field}
                                placeholder="Ex: Galena"
                                error={!!registerErrors.company}
                                helperText={registerErrors.company?.message}
                              />
                            </Stack>
                          )}
                        />

                        {company?.check_employee_code && (
                          <Controller
                            name="employee_code"
                            control={registerControl}
                            render={({ field }) => (
                              <Stack>
                                <FormLabel>
                                  Qual é o seu nº de matrícula?
                                </FormLabel>
                                <TextField
                                  {...field}
                                  placeholder="Insira aqui o número da sua matrícula"
                                  error={!!registerErrors.employee_code}
                                  helperText={
                                    registerErrors.employee_code?.message
                                  }
                                />
                              </Stack>
                            )}
                          />
                        )}

                        <Stack direction="row" justifyContent="end">
                          <Button
                            type="submit"
                            variant="contained"
                            disabled={isRegisterPending}
                          >
                            {isRegisterPending ? (
                              <CircularProgress size={20} />
                            ) : (
                              'Cadastrar'
                            )}
                          </Button>
                        </Stack>
                      </Stack>
                    </Box>
                  )}
                </Stack>
              </ContentBox>
            </Grid>

            <Grid size={{ xs: 12, lg: 6 }}>
              <ContentBox>
                <Stack alignItems="center" p={5} spacing={2}>
                  <Typography variant="h2" mb={2}>
                    Já possui uma conta?
                  </Typography>

                  {expanded !== 'login' ? (
                    <Button
                      variant="outlined"
                      endIcon={<ExpandMore />}
                      onClick={() => setExpanded('login')}
                    >
                      Acessar agora
                    </Button>
                  ) : (
                    <Box
                      component="form"
                      onSubmit={handleLoginSubmit(onLoginSubmit)}
                      width="100%"
                      sx={{
                        maxHeight: expanded === 'login' ? '600px' : '0',
                        overflow: 'hidden',
                        transition: 'max-height 0.2s ease-out',
                      }}
                    >
                      <Typography mb={2}>
                        Se você já se cadastrou em algum momento, preencha as
                        informações abaixo e acesse o seu benefício.
                      </Typography>

                      <Typography mb={3}>
                        Caso tenha esquecido a sua senha, clique em "esqueci
                        minha senha" para redefini-la (:
                      </Typography>

                      <Stack spacing={3}>
                        <Controller
                          name="email"
                          control={loginControl}
                          render={({ field }) => (
                            <Stack>
                              <FormLabel>E-mail ou CPF</FormLabel>
                              <TextField
                                {...field}
                                placeholder="Insira seu e-mail ou seu cpf"
                                error={!!loginErrors.email}
                                helperText={loginErrors.email?.message}
                              />
                            </Stack>
                          )}
                        />

                        <Controller
                          name="password"
                          control={loginControl}
                          render={({ field }) => (
                            <Stack>
                              <FormLabel>Senha</FormLabel>
                              <TextField
                                {...field}
                                type={showPassword ? 'text' : 'password'}
                                placeholder="Insira a senha aqui"
                                error={!!loginErrors.password}
                                helperText={loginErrors.password?.message}
                                slotProps={{
                                  input: {
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <IconButton
                                          aria-label="toggle password visibility"
                                          onClick={() =>
                                            setShowPassword(!showPassword)
                                          }
                                          edge="end"
                                        >
                                          {showPassword ? (
                                            <VisibilityOff />
                                          ) : (
                                            <Visibility />
                                          )}
                                        </IconButton>
                                      </InputAdornment>
                                    ),
                                  },
                                }}
                              />
                            </Stack>
                          )}
                        />

                        <Stack
                          direction={{ xs: 'column-reverse', sm: 'row' }}
                          spacing={2}
                          justifyContent="end"
                          alignItems="center"
                        >
                          <Button variant="text" onClick={onForgotPassword}>
                            Esqueci minha senha
                          </Button>
                          <Button
                            type="submit"
                            variant="contained"
                            disabled={isLoginPending}
                          >
                            {isLoginPending ? (
                              <CircularProgress size={20} />
                            ) : (
                              'Entrar'
                            )}
                          </Button>
                        </Stack>
                      </Stack>
                    </Box>
                  )}
                </Stack>
              </ContentBox>
            </Grid>
          </Grid>

          <Stack mt={4} spacing={2} alignItems="center">
            <Typography fontWeight={600} color="white" textAlign="center">
              Dúvidas?
            </Typography>
            <Typography fontWeight={600} color="white" textAlign="center">
              Fale conosco
            </Typography>
            <Link
              href="https://wa.me/5511987566079"
              target="_blank"
              rel="noopener noreferrer"
              textAlign="center"
            >
              <img
                src={getSvg('logo-whatsapp.svg')}
                alt="WhatsApp"
                style={{ margin: '16px' }}
              />
            </Link>
          </Stack>

          <Stack mb={4} spacing={2} alignItems="center">
            <Typography fontWeight={600} color="white" textAlign="center">
              Saiba mais
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center" mt={2}>
              <Link
                href="https://www.linkedin.com/company/plataformagalena/mycompany/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={getSvg('logo-linkedin.svg')} alt="LinkedIn" />
              </Link>
              <Link
                href="https://www.instagram.com/plataformagalena/?igsh=dW1hNG9scDFkODFi"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={getSvg('logo-instagram.svg')} alt="Instagram" />
              </Link>
              <Link
                href="https://galena.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={getSvg('globe-outline.svg')} alt="Website" />
              </Link>
            </Stack>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
};
